import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import AntToolbar from './AntToolbar';
import AntLocalization from '../utils/AntLocalization';
import AntWidgetSkeleton from './AntWidgetSkeleton';
import useWidgetFirstLoad from './AntWidgetSkeleton/useWidgetFirstLoad';

var DAY_LABELS = [
    { vi: 'T2', en: 'Mon', fr: 'Lun', es: 'Lun', id: 'Sen', ja: '月', zh: '一', ko: '월', hi: 'सोम', ar: 'اثن' },
    { vi: 'T3', en: 'Tue', fr: 'Mar', es: 'Mar', id: 'Sel', ja: '火', zh: '二', ko: '화', hi: 'मंगल', ar: 'ثلا' },
    { vi: 'T4', en: 'Wed', fr: 'Mer', es: 'Mié', id: 'Rab', ja: '水', zh: '三', ko: '수', hi: 'बुध', ar: 'أرب' },
    { vi: 'T5', en: 'Thu', fr: 'Jeu', es: 'Jue', id: 'Kam', ja: '木', zh: '四', ko: '목', hi: 'गुरु', ar: 'خمي' },
    { vi: 'T6', en: 'Fri', fr: 'Ven', es: 'Vie', id: 'Jum', ja: '金', zh: '五', ko: '금', hi: 'शुक्र', ar: 'جمع' },
    { vi: 'T7', en: 'Sat', fr: 'Sam', es: 'Sáb', id: 'Sab', ja: '土', zh: '六', ko: '토', hi: 'शनि', ar: 'سبت' },
    { vi: 'CN', en: 'Sun', fr: 'Dim', es: 'Dom', id: 'Min', ja: '日', zh: '日', ko: '일', hi: 'रवि', ar: 'أحد' }
];

var T_ORDERS = { vi: 'Đơn hàng', en: 'Orders', fr: 'Commandes', es: 'Pedidos', id: 'Pesanan', ja: '注文', zh: '订单', ko: '주문', hi: 'ऑर्डर', ar: 'طلبات' };
var T_REVENUE = { vi: 'Doanh thu', en: 'Revenue', fr: 'Revenus', es: 'Ingresos', id: 'Pendapatan', ja: '収益', zh: '收入', ko: '매출', hi: 'राजस्व', ar: 'الإيرادات' };
var T_LOW = { vi: 'Ít', en: 'Low', fr: 'Bas', es: 'Bajo', id: 'Rendah', ja: '少', zh: '少', ko: '낮음', hi: 'कम', ar: 'منخفض' };
var T_HIGH = { vi: 'Nhiều', en: 'High', fr: 'Haut', es: 'Alto', id: 'Tinggi', ja: '多', zh: '多', ko: '높음', hi: 'अधिक', ar: 'مرتفع' };

// 6-stop blue-intensity ramp [r, g, b]
var SCALE = [
    [241, 245, 249],  // #f1f5f9 — empty
    [219, 234, 254],  // #dbeafe — very low
    [147, 197, 253],  // #93c5fd — low
    [79, 142, 247],   // #4f8ef7 — medium
    [37, 99, 235],    // #2563eb — high
    [30, 58, 138]     // #1e3a8a — peak
];

function cellColor(ratio) {
    if (ratio <= 0) return 'rgb(241,245,249)';
    if (ratio >= 1) return 'rgb(30,58,138)';
    var pos = ratio * (SCALE.length - 1);
    var i = Math.floor(pos);
    var t = pos - i;
    var a = SCALE[i], b = SCALE[Math.min(i + 1, SCALE.length - 1)];
    return `rgb(${Math.round(a[0] + (b[0] - a[0]) * t)},${Math.round(a[1] + (b[1] - a[1]) * t)},${Math.round(a[2] + (b[2] - a[2]) * t)})`;
}

var SHOW_HOURS = [0, 3, 6, 9, 12, 15, 18, 21];

export default function AntWidgetHeatmap(props) {
    var [cells, setCells] = useState([]);
    var [hovered, setHovered] = useState(null);
    var wrapRef = useRef(null);
    var [loaded, markLoaded] = useWidgetFirstLoad(Boolean(props.url));

    var valueField = props.valueField || 'order_count';

    var load = useCallback(async () => {
        if (props.url) {
            props.API.post(props.url, { ...props.searchConditions }, (resp) => {
                if (resp && !resp.error && resp.data) {
                    var rows = resp.data instanceof Array ? resp.data : resp.data.rows || [];
                    setCells(rows);
                    if (props.onEmpty) props.onEmpty(rows.length === 0);
                } else {
                    setCells([]);
                    if (props.onEmpty) props.onEmpty(true);
                }
                markLoaded();
            });
        }
    }, [props.API, props.url, props.searchConditions, markLoaded]);

    useEffect(() => {
        load();
        var interval = setInterval(load, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => { load(); }, [props.searchConditions, props.url]);

    // Build 7×24 matrix
    var { matrix, maxVal } = useMemo(() => {
        var m = Array.from({ length: 7 }, () => Array(24).fill(null));
        var max = 0;
        for (var c of cells) {
            var d = c.day, h = c.hour;
            if (d >= 0 && d < 7 && h >= 0 && h < 24) {
                m[d][h] = c;
                var v = parseFloat(c[valueField]) || 0;
                if (v > max) max = v;
            }
        }
        return { matrix: m, maxVal: max };
    }, [cells, valueField]);

    var hoveredData = hovered ? (matrix[hovered.day] && matrix[hovered.day][hovered.hour]) : null;

    if (!loaded) {
        return (
            <React.Fragment>
                {props.title && <AntToolbar title={props.title} isH6={true} upperCase={true} />}
                <AntWidgetSkeleton variant="heatmap" />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            {props.title && <AntToolbar title={props.title} isH6={true} upperCase={true} />}
            <div className="ant-widget-heatmap" ref={wrapRef}>
                <div className="ant-widget-heatmap__grid">
                    {/* Hour labels */}
                    <div className="ant-widget-heatmap__row ant-widget-heatmap__row--header">
                        <div className="ant-widget-heatmap__day-label" />
                        {Array.from({ length: 24 }, (_, h) => (
                            <div key={h} className="ant-widget-heatmap__hour-label">
                                {SHOW_HOURS.includes(h) ? h : ''}
                            </div>
                        ))}
                    </div>

                    {/* Day rows */}
                    {matrix.map((row, dayIdx) => (
                        <div key={dayIdx} className="ant-widget-heatmap__row">
                            <div className="ant-widget-heatmap__day-label">
                                {AntLocalization.translateText(DAY_LABELS[dayIdx])}
                            </div>
                            {row.map((cell, hourIdx) => {
                                var val = cell ? (parseFloat(cell[valueField]) || 0) : 0;
                                var ratio = maxVal > 0 ? val / maxVal : 0;
                                var isHov = hovered && hovered.day === dayIdx && hovered.hour === hourIdx;
                                return (
                                    <div
                                        key={hourIdx}
                                        className={'ant-widget-heatmap__cell' + (isHov ? ' ant-widget-heatmap__cell--hover' : '')}
                                        style={{ background: cellColor(ratio) }}
                                        onMouseEnter={(e) => {
                                            var rect = e.currentTarget.getBoundingClientRect();
                                            var parentRect = wrapRef.current.getBoundingClientRect();
                                            setHovered({
                                                day: dayIdx,
                                                hour: hourIdx,
                                                x: rect.left - parentRect.left + rect.width / 2,
                                                y: rect.top - parentRect.top
                                            });
                                        }}
                                        onMouseLeave={() => setHovered(null)}
                                    />
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Legend */}
                <div className="ant-widget-heatmap__legend">
                    <span className="ant-widget-heatmap__legend-label">{AntLocalization.translateText(T_LOW)}</span>
                    <div className="ant-widget-heatmap__legend-bar" />
                    <span className="ant-widget-heatmap__legend-label">{AntLocalization.translateText(T_HIGH)}</span>
                </div>

                {/* Tooltip */}
                {hovered && hoveredData && (
                    <div className="ant-widget-heatmap__tooltip" style={{ left: hovered.x, top: hovered.y - 4 }}>
                        <div className="ant-widget-heatmap__tooltip-time">
                            {AntLocalization.translateText(DAY_LABELS[hovered.day]) + ' ' + String(hovered.hour).padStart(2, '0') + ':00' + '\u2013' + String(hovered.hour).padStart(2, '0') + ':59'}
                        </div>
                        <div className="ant-widget-heatmap__tooltip-row">
                            <span>{AntLocalization.translateText(T_ORDERS)}</span>
                            <span>{AntLocalization.translateInteger(hoveredData.order_count || 0)}</span>
                        </div>
                        <div className="ant-widget-heatmap__tooltip-row">
                            <span>{AntLocalization.translateText(T_REVENUE)}</span>
                            <span>{AntLocalization.translateCurrency(hoveredData.revenue || 0)}</span>
                        </div>
                    </div>
                )}
            </div>
        </React.Fragment>
    );
}
